import { type ReactNode, type FC, useMemo } from 'react';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { Divider } from '@/shared/components/Divider';
import { ChartLegend } from '@/shared/components/molecules/ChartLegend';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';

type StatProps = {
  title: string;
  value: string | ReactNode;
  subValue?: string;
  caption?: ReactNode;
  timeframe?: ReactNode;
  isLoading: boolean;
  tooltip?: string;
};

export const StatsWithChart = <RawData, ChartData, Options extends Record<string, unknown>>({
  title,
  rightContent,
  stats,
  isLoading,
  Chart,
  rawData,
  aggregateData,
  aggregateOptions,
  legendLabels,
}: {
  title: string;
  rightContent?: React.JSX.Element;
  stats: StatProps[];
  isLoading: boolean;
  Chart: FC<{ data: ChartData; isLoading: boolean }>;
  rawData: RawData;
  aggregateData: (data: RawData, options: Options) => ChartData;
  aggregateOptions: Options;
  legendLabels: {
    label: string;
    color: string;
  }[];
}) => {
  const chartData = useMemo(
    () => aggregateData(rawData, aggregateOptions),
    [rawData, aggregateOptions, aggregateData],
  );

  return (
    <div className="flex w-full flex-col rounded-md border border-cf-gray-4 bg-cf-gray-3">
      <div className="flex items-center justify-between px-6 py-4">
        <span className="font-aeonikMedium text-cf-white">{title}</span>
        {rightContent}
      </div>
      <Divider className="h-px" />
      <div className="flex flex-wrap items-start gap-4 p-6 md:gap-x-8">
        {stats.length > 0 &&
          stats.map((stat, i) => (
            <div className="flex items-start gap-x-4 md:gap-x-8" key={stat.title}>
              <div className="flex flex-col gap-y-2 font-aeonikMedium text-12 text-cf-light-2">
                <div className="flex items-center gap-x-1">
                  {stat.title}
                  <QuestionMarkTooltip content={stat.tooltip} />
                </div>
                <div className="flex items-baseline gap-x-1">
                  {stat.isLoading ? (
                    <SkeletonLine width={100} height={20} />
                  ) : (
                    <>
                      <span className="text-14 text-cf-white md:text-20">{stat.value}</span>
                      {stat.subValue && <span>{stat.subValue}</span>}
                    </>
                  )}
                </div>
                {stat.caption ?? stat.timeframe}
              </div>
              {stats.length > 1 && i !== stats.length - 1 && (
                <div className="h-[82px] w-px border-r border-cf-gray-4" />
              )}
            </div>
          ))}
      </div>
      <div className="p-6">
        <Chart data={chartData} isLoading={isLoading} />
        <div className="mt-3">
          <ChartLegend items={legendLabels} />
        </div>
      </div>
    </div>
  );
};
